import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, BadgeCheck, Building2, Calendar, Clock, FileText, Mail, MapPin, Phone, User } from "lucide-react";
import { EmployeeDetail } from "@/types/employee";
import { MFOItem } from "@/types/kanban";
import MFODetailDialog from "@/components/mfo/MFODetailDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

const DEPARTMENT_NAMES: Record<string, string> = {
  "dept-tourism": "Tourism Department",
  "dept-culture": "Cultural Affairs",
  "dept-events": "Events Management"
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "active":
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <BadgeCheck className="h-3 w-3" /> Active
        </Badge>
      );
    case "inactive":
      return (
        <Badge className="bg-gray-100 text-gray-800 flex items-center gap-1">
          <Clock className="h-3 w-3" /> Inactive
        </Badge>
      );
    case "on-leave":
      return (
        <Badge className="bg-amber-100 text-amber-800 flex items-center gap-1">
          <Calendar className="h-3 w-3" /> On Leave
        </Badge>
      );
    default:
      return (
        <Badge className="bg-gray-100 text-gray-800 flex items-center gap-1">
          <Clock className="h-3 w-3" /> {status}
        </Badge>
      );
  }
};

const getProgressColor = (progress: number) => {
  if (progress < 30) return "bg-red-500";
  if (progress < 70) return "bg-amber-500";
  return "bg-green-500";
};

const EmployeeDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [selectedMFO, setSelectedMFO] = useState<MFOItem | null>(null);
  const [isMFODialogOpen, setIsMFODialogOpen] = useState(false);
  
  useEffect(() => {
    const fetchEmployee = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        const { data: employeeData, error: employeeError } = await supabase
          .from('employees')
          .select(`
            *,
            department:departments(id, name)
          `)
          .eq('id', id)
          .single();
          
        if (employeeError) throw employeeError;
        
        if (!employeeData) {
          toast({
            title: "Employee not found",
            description: "We couldn't find the employee you're looking for.",
            variant: "destructive",
          });
          navigate("/dashboard/employees");
          return;
        }
        
        const { data: skillsData, error: skillsError } = await supabase
          .from('employee_skills')
          .select('id, skill')
          .eq('employee_id', id);
          
        if (skillsError) throw skillsError;
        
        const { data: mfosData, error: mfosError } = await supabase
          .from('mfo_assignees')
          .select(`
            mfo:mfo_items(
              id, code, title, description, status, priority, progress, 
              due_date, budget, department_id, q1, q2, q3, q4,
              success_indicators:mfo_success_indicators(indicator)
            )
          `)
          .eq('employee_id', id);
          
        if (mfosError) throw mfosError;
        
        const formattedMFOs = mfosData
          .filter(item => item.mfo)
          .map(item => {
            const mfo = item.mfo;
            return {
              id: mfo.id,
              code: mfo.code,
              title: mfo.title,
              successIndicators: mfo.success_indicators?.map((si: any) => si.indicator) || [mfo.description || ""],
              budget: mfo.budget,
              timeline: { 
                q1: mfo.q1, 
                q2: mfo.q2, 
                q3: mfo.q3, 
                q4: mfo.q4 
              },
              collaborators: [],
              departmentId: mfo.department_id,
              status: mfo.status,
              priority: mfo.priority,
              progress: mfo.progress,
              dueDate: mfo.due_date,
              due_date: mfo.due_date
            };
          });
        
        const processedSkills = skillsData ? 
          skillsData.map((skill: any) => typeof skill === 'string' ? { id: `skill-${Math.random()}`, skill } : skill) :
          [];
        
        const employeeDetail: EmployeeDetail = {
          id: employeeData.id,
          name: employeeData.name,
          email: employeeData.email,
          position: employeeData.position,
          department_id: employeeData.department_id,
          departmentId: employeeData.department_id,
          departmentName: employeeData.department?.name,
          phone: employeeData.phone || "",
          start_date: employeeData.start_date,
          startDate: employeeData.start_date,
          status: employeeData.status as "active" | "inactive" | "on-leave",
          bio: employeeData.bio || "",
          avatar: employeeData.avatar,
          skills: processedSkills,
          mfos: formattedMFOs
        };
        
        setEmployee(employeeDetail);
      } catch (error) {
        console.error("Error fetching employee:", error);
        toast({
          title: "Error",
          description: "Failed to load employee data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchEmployee();
  }, [id, navigate, toast]);
  
  const openMFODialog = (mfo: MFOItem) => {
    setSelectedMFO(mfo);
    setIsMFODialogOpen(true);
  };
  
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <Button 
            variant="outline" 
            size="sm"
            className="mr-3"
            onClick={() => navigate("/dashboard/employees")}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">Employee Profile</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Skeleton className="h-[600px] rounded-lg" />
          </div>
          <div className="md:col-span-2">
            <Skeleton className="h-[600px] rounded-lg" />
          </div>
        </div>
      </div>
    );
  }
  
  if (!employee) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh]">
        <h2 className="text-2xl font-bold mb-4">Employee not found</h2>
        <Button onClick={() => navigate("/dashboard/employees")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Employees
        </Button>
      </div>
    );
  }
  
  if (employee) {
    const formattedStartDate = new Date(employee.start_date || employee.startDate || "").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });

    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <Button 
            variant="outline" 
            size="sm"
            className="mr-3"
            onClick={() => navigate("/dashboard/employees")}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">Employee Profile</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center mb-6">
                <Avatar className="h-24 w-24 mb-4">
                  <AvatarImage src={employee.avatar || ""} alt={employee.name} />
                  <AvatarFallback className="text-xl">
                    {employee.name.split(" ").map(n => n[0]).join("")}
                  </AvatarFallback>
                </Avatar>
                <h3 className="text-xl font-bold">{employee.name}</h3>
                <p className="text-muted-foreground">{employee.position}</p>
                <div className="mt-2">{getStatusBadge(employee.status as "active" | "inactive" | "on-leave")}</div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{employee.departmentName || DEPARTMENT_NAMES[employee.department_id || ""] || "Not Assigned"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{employee.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{employee.phone || "Not available"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Started {formattedStartDate}</span>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium mb-2">Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {employee.skills?.length > 0 ? (
                    Array.isArray(employee.skills) && employee.skills.map((skill, index) => (
                      <Badge key={index} variant="secondary" className="bg-secondary/50">
                        {typeof skill === 'string' ? skill : skill.skill}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No skills listed</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="md:col-span-2 space-y-6">
            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">
                  <User className="h-4 w-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="mfos">
                  <FileText className="h-4 w-4 mr-2" />
                  MFOs
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Bio</CardTitle>
                    <CardDescription>
                      Professional background and expertise
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p>{employee.bio || "No bio information available."}</p>
                  </CardContent>
                </Card>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">MFOs Overview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Assigned MFOs</span>
                            <span className="font-medium">{employee.mfos.length}</span>
                          </div>
                          <Progress value={100} className="bg-primary/20" />
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>In Progress</span>
                            <span className="font-medium">
                              {employee.mfos.filter(m => m.status === "in-progress").length}
                            </span>
                          </div>
                          <Progress 
                            value={
                              employee.mfos.length ? 
                              (employee.mfos.filter(m => m.status === "in-progress").length / 
                              employee.mfos.length) * 100 : 0
                            } 
                            className="bg-blue-200"
                          />
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Completed</span>
                            <span className="font-medium">
                              {employee.mfos.filter(m => m.status === "completed").length}
                            </span>
                          </div>
                          <Progress 
                            value={
                              employee.mfos.length ?
                              (employee.mfos.filter(m => m.status === "completed").length / 
                              employee.mfos.length) * 100 : 0
                            } 
                            className="bg-green-200"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="grid grid-cols-[24px_1fr] gap-4 items-center">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">Email</p>
                            <p className="text-sm text-muted-foreground">
                              {employee.email}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-[24px_1fr] gap-4 items-center">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">Phone</p>
                            <p className="text-sm text-muted-foreground">
                              {employee.phone || "Not available"}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-[24px_1fr] gap-4 items-center">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">Office</p>
                            <p className="text-sm text-muted-foreground">
                              Municipal Hall, Room 205
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="mfos" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Assigned MFOs</CardTitle>
                    <CardDescription>
                      Major Final Outputs assigned to this employee
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {employee.mfos.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No MFOs assigned to this employee yet
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {employee.mfos.map((mfo) => (
                          <div 
                            key={mfo.id} 
                            className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline">{mfo.code}</Badge>
                                  <h4 className="font-medium">{mfo.title}</h4>
                                </div>
                                <p className="text-sm text-muted-foreground mb-3">
                                  {mfo.successIndicators && mfo.successIndicators.length > 0 ? mfo.successIndicators[0] : ""}
                                </p>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={
                                  mfo.status === "completed" ? "bg-green-100 text-green-800" :
                                  mfo.status === "in-progress" ? "bg-blue-100 text-blue-800" :
                                  mfo.status === "delayed" ? "bg-amber-100 text-amber-800" :
                                  "bg-gray-100 text-gray-800"
                                }
                              >
                                {mfo.status.replace('-', ' ')}
                              </Badge>
                            </div>
                            
                            <div className="space-y-2 mt-2">
                              <div>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-muted-foreground">Progress</span>
                                  <span>{mfo.progress}%</span>
                                </div>
                                <Progress value={mfo.progress} className={getProgressColor(mfo.progress)} />
                              </div>
                              
                              <div className="flex justify-between items-center mt-4">
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  Due: {mfo.dueDate || mfo.due_date}
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => openMFODialog(mfo as MFOItem)}
                                >
                                  View MFO
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <MFODetailDialog 
          mfo={selectedMFO} 
          isOpen={isMFODialogOpen} 
          onOpenChange={setIsMFODialogOpen} 
        />
      </div>
    );
  }

  return null;
};

export default EmployeeDetailPage;
